import { useEffect, useState } from 'react';
import { Alert, Button, Empty, Popconfirm, Skeleton, Space, Tooltip, Typography } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Collapse } from 'react-collapse';
import { AUTH0_TOKEN_URL, BASE_URL } from 'shared/constants';
import {
  useCreatePartnerApplication,
  useDeletePartnerApplication,
  usePartnerApplication,
  useRotatePartnerApplicationSecret
} from 'api/partnerAclAdmin';
import { useScopes } from 'hooks/useScopes';
import { PartnerAclPartner } from 'types/PartnerAclAdmin';
import './PartnerAclApplicationPanel.scss';

interface PartnerAclApplicationPanelProps {
  partner: PartnerAclPartner;
}

const PartnerAclApplicationPanel: React.FC<PartnerAclApplicationPanelProps> = ({ partner }) => {
  const { aclCheck } = useScopes();
  const canCreate = aclCheck(['create:partner_acl_management', 'update:site_wide_admin']);
  const canDelete = aclCheck(['delete:partner_acl_management', 'update:site_wide_admin']);

  const [isSecretVisible, setIsSecretVisible] = useState(false);

  const { data: application, isLoading, refetch } = usePartnerApplication(partner.id);
  const { mutateAsync: createApplication, isPending: isCreating } = useCreatePartnerApplication(partner.id);
  const { mutateAsync: deleteApplication, isPending: isDeleting } = useDeletePartnerApplication(partner.id);
  const { mutateAsync: rotateSecret, isPending: isRotating } = useRotatePartnerApplicationSecret(partner.id);

  const isSaving = isCreating || isDeleting || isRotating;

  useEffect(() => {
    setIsSecretVisible(false);
  }, [partner.id, application?.client_id]);

  const handleCreate = async () => {
    await createApplication();
    setIsSecretVisible(true);
  };

  const handleDelete = async () => {
    await deleteApplication();
    setIsSecretVisible(false);
  };

  const handleRotateSecret = async () => {
    await rotateSecret();
    setIsSecretVisible(true);
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-4 rounded-xl"
        message="Partner M2M credentials"
        description="Create and manage the Auth0 machine-to-machine application used by this partner to call Vantage partner APIs."
      />

      <Skeleton loading={isLoading} active paragraph={{ rows: 8 }}>
        <div className="partner-acl-application">
          <Collapse isOpened={!application}>
            <Empty description="No Auth0 application exists for this partner.">
              {canCreate && (
                <Button type="primary" size="large" loading={isSaving} onClick={handleCreate}>
                  Create Application
                </Button>
              )}
            </Empty>
          </Collapse>

          <Collapse isOpened={Boolean(application)}>
            {application && (
              <div>
                <div className="mb-6">
                  <Typography.Text type="secondary">Application name</Typography.Text>
                  <div>
                    <Typography.Text strong>{application.name}</Typography.Text>
                  </div>
                </div>

                <div className="mb-6">
                  <Typography.Text type="secondary">Client ID</Typography.Text>
                  <div>
                    <Typography.Text copyable code className="text-base">
                      {application.client_id}
                    </Typography.Text>
                  </div>
                </div>

                <div className="mb-6">
                  <Typography.Text type="secondary">Client secret</Typography.Text>
                  <div>
                    <Typography.Text copyable code className={`text-base ${isSecretVisible ? '' : 'secure'}`}>
                      {application.client_secret}
                    </Typography.Text>
                    <Tooltip title={isSecretVisible ? 'Hide secret' : 'Show secret'}>
                      <Button
                        type="link"
                        size="small"
                        icon={isSecretVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => setIsSecretVisible((prev) => !prev)}
                      />
                    </Tooltip>
                  </div>
                </div>

                <div className="mb-6">
                  <Typography.Text type="secondary">Audience</Typography.Text>
                  <div>
                    <Typography.Text copyable code className="text-base">
                      {application.audience}
                    </Typography.Text>
                  </div>
                </div>

                <div className="mb-6">
                  <Typography.Text type="secondary">Grant type</Typography.Text>
                  <div>
                    <Typography.Text copyable code className="text-base">
                      client_credentials
                    </Typography.Text>
                  </div>
                </div>

                <div className="mb-6">
                  <Typography.Text type="secondary">Token URL</Typography.Text>
                  <div>
                    <Typography.Text copyable code className="text-base">
                      {AUTH0_TOKEN_URL}
                    </Typography.Text>
                  </div>
                </div>

                <div className="mb-6">
                  <Typography.Text type="secondary">Base URL for API</Typography.Text>
                  <div>
                    <Typography.Text copyable code className="text-base">
                      {BASE_URL}
                    </Typography.Text>
                  </div>
                </div>

                <Space wrap>
                  {canCreate && (
                    <Popconfirm
                      title="Rotate the client secret?"
                      description="Existing integrations must be updated with the new secret."
                      onConfirm={handleRotateSecret}
                      okButtonProps={{ loading: isRotating }}
                    >
                      <Button danger type="primary" size="large" loading={isSaving}>
                        Rotate Secret
                      </Button>
                    </Popconfirm>
                  )}
                  {canDelete && (
                    <Popconfirm
                      title="Delete this partner application?"
                      description="This revokes API access until a new application is created."
                      onConfirm={handleDelete}
                      okButtonProps={{ loading: isDeleting }}
                    >
                      <Button danger size="large" loading={isSaving}>
                        Delete Application
                      </Button>
                    </Popconfirm>
                  )}
                  <Button size="large" onClick={() => refetch()} loading={isLoading}>
                    Refresh
                  </Button>
                </Space>
              </div>
            )}
          </Collapse>
        </div>
      </Skeleton>
    </div>
  );
};

export default PartnerAclApplicationPanel;
